import React, { useState, useEffect } from 'react';
import {
  BadgeCheck,
  Bell,
  CircleHelp,
  Clock3,
  Cookie,
  Mail,
  MessageCircle,
  SlidersHorizontal,
  Sparkles,
  UsersRound,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import UserSectionSidebar from '../components/UserSectionSidebar';
import { useTranslations } from '../translations';
import { isAnalyticsConfigured } from '../services/analytics';
import { openCookiePreferences } from '../services/analyticsConsent';
import { getAccessToken, setUserAccountType as storeUserAccountType } from '../services/auth';
import { useNotification } from '../components/NotificationProvider';
import {
  fetchNotificationSettings,
  fetchUserProfile,
  updateNotificationSetting,
  updateUserAccountType,
  NotificationSetting,
} from '../services/user.api';
import type { UpdateUserAccountTypeRequest, UserAccountType } from '../services/api.types';
import LoadingIndicator from '../components/LoadingIndicator';
import AccountTypeForm, {
  AccountTypeFormErrors,
  accountTypeFormFromProfile,
  validateAccountTypeForm,
} from '../components/AccountTypeForm';
import './Settings.css';

interface SettingsProps {
  isLoggedIn: boolean;
  isAuthChecking: boolean;
  isAdmin?: boolean;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToLocations?: () => void;
  onNavigateToMyAnnouncements?: () => void;
  onNavigateToAdministration?: () => void;
  onLogout: () => void;
  onNavigateToCreateProduct?: () => void;
  onNavigateToMessages?: (productId?: string | null) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
  onNavigateToAbout?: () => void;
  onAccountTypeChange?: (accountType: UserAccountType) => void;
}

interface NotificationSettings {
  marketingEmails: boolean;
  productActivatedNotifications: boolean;
  productDeclinedNotifications: boolean;
  productExpiredNotifications: boolean;
  chatNotifications: boolean;
}

type SettingsSection = 'account' | 'notifications';

const Settings: React.FC<SettingsProps> = ({
  isLoggedIn,
  isAuthChecking,
  isAdmin = false,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onNavigateToProfile,
  onNavigateToLocations,
  onNavigateToMyAnnouncements,
  onNavigateToAdministration,
  onLogout,
  onNavigateToCreateProduct,
  onNavigateToMessages,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
  onAccountTypeChange,
}) => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [activeSettingsSection, setActiveSettingsSection] = useState<SettingsSection>('account');
  const [accountTypeForm, setAccountTypeForm] = useState<UpdateUserAccountTypeRequest | null>(null);
  const [savedAccountTypeForm, setSavedAccountTypeForm] = useState<UpdateUserAccountTypeRequest | null>(null);
  const [hasSelectedAccountType, setHasSelectedAccountType] = useState(false);
  const [savedHasSelectedAccountType, setSavedHasSelectedAccountType] = useState(false);
  const [accountTypeErrors, setAccountTypeErrors] = useState<AccountTypeFormErrors>({});
  const [isSavingAccountType, setIsSavingAccountType] = useState(false);
  const [settings, setSettings] = useState<NotificationSettings>({
    marketingEmails: true,
    productActivatedNotifications: true,
    productDeclinedNotifications: true,
    productExpiredNotifications: true,
    chatNotifications: true,
  });

  useEffect(() => {
    const loadSettings = async () => {
      if (isAuthChecking) {
        return;
      }
      const token = getAccessToken();
      if (!token) {
        onNavigateToLogin();
        return;
      }

      try {
        const [response, profile] = await Promise.all([
          fetchNotificationSettings(),
          fetchUserProfile(),
        ]);
        const loadedAccountType = accountTypeFormFromProfile(profile);
        const hasDeclaredAccountType = Boolean(profile.accountType);
        setAccountTypeForm(loadedAccountType);
        setSavedAccountTypeForm(loadedAccountType);
        setHasSelectedAccountType(hasDeclaredAccountType);
        setSavedHasSelectedAccountType(hasDeclaredAccountType);

        // Convert API response to settings state
        const newSettings: NotificationSettings = {
          marketingEmails: true,
          productActivatedNotifications: true,
          productDeclinedNotifications: true,
          productExpiredNotifications: true,
          chatNotifications: true,
        };

        response.forEach((setting: NotificationSetting) => {
          switch (setting.notificationType) {
            case 'MARKETING':
              newSettings.marketingEmails = setting.status;
              break;
            case 'PRODUCT_ACTIVATED':
              newSettings.productActivatedNotifications = setting.status;
              break;
            case 'PRODUCT_DECLINED':
              newSettings.productDeclinedNotifications = setting.status;
              break;
            case 'PRODUCT_EXPIRED':
              newSettings.productExpiredNotifications = setting.status;
              break;
            case 'CHAT_MESSAGES':
              newSettings.chatNotifications = setting.status;
              break;
          }
        });

        setSettings(newSettings);
        setLoading(false);
      } catch (err) {
        console.error('Error loading settings:', err);
        const errorMessage = err instanceof Error ? err.message : '';
        if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
          onNavigateToLogin();
          return;
        }
        showNotification('Eroare la încărcarea setărilor', 'error');
        setLoading(false);
      }
    };

    loadSettings();
  }, [isAuthChecking, onNavigateToLogin, showNotification]);

  const handleToggle = async (setting: keyof NotificationSettings) => {
    const newValue = !settings[setting];

    // Optimistically update UI
    setSettings(prev => ({
      ...prev,
      [setting]: newValue
    }));

    try {
      // Map setting key to API notification type
      let notificationType: NotificationSetting['notificationType'];
      switch (setting) {
        case 'marketingEmails':
          notificationType = 'MARKETING';
          break;
        case 'productActivatedNotifications':
          notificationType = 'PRODUCT_ACTIVATED';
          break;
        case 'productDeclinedNotifications':
          notificationType = 'PRODUCT_DECLINED';
          break;
        case 'productExpiredNotifications':
          notificationType = 'PRODUCT_EXPIRED';
          break;
        case 'chatNotifications':
          notificationType = 'CHAT_MESSAGES';
          break;
        default:
          return;
      }

      await updateNotificationSetting(notificationType, newValue);
      showNotification('Setările au fost actualizate cu succes!', 'success');
    } catch (err) {
      console.error('Error updating setting:', err);
      // Revert on error
      setSettings(prev => ({
        ...prev,
        [setting]: !newValue
      }));
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      showNotification('Eroare la actualizarea setărilor', 'error');
    }
  };

  const handleAccountTypeSave = async () => {
    if (!accountTypeForm || isSavingAccountType) return;
    if (!hasSelectedAccountType) {
      showNotification(t.settings.accountTypeSelectionRequired, 'error');
      return;
    }
    const validationErrors = validateAccountTypeForm(accountTypeForm);
    if (Object.keys(validationErrors).length > 0) {
      setAccountTypeErrors(validationErrors);
      showNotification(t.accountType.requiredFields, 'error');
      return;
    }

    setIsSavingAccountType(true);
    setAccountTypeErrors({});
    try {
      await updateUserAccountType(accountTypeForm);
      storeUserAccountType(accountTypeForm.accountType);
      const savedForm = { ...accountTypeForm, declarationAccepted: true };
      setAccountTypeForm(savedForm);
      setSavedAccountTypeForm(savedForm);
      setHasSelectedAccountType(true);
      setSavedHasSelectedAccountType(true);
      onAccountTypeChange?.(accountTypeForm.accountType);
      showNotification(t.settings.accountTypeSaveSuccess, 'success');
    } catch (err) {
      console.error('Error updating account type:', err);
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      showNotification(t.settings.accountTypeSaveError, 'error');
    } finally {
      setIsSavingAccountType(false);
    }
  };

  const accountTypeHasChanges = Boolean(
    accountTypeForm
    && savedAccountTypeForm
    && hasSelectedAccountType
    && (
      hasSelectedAccountType !== savedHasSelectedAccountType
      || JSON.stringify(accountTypeForm) !== JSON.stringify(savedAccountTypeForm)
    )
  );

  const handleAccountTypeFormChange = (nextForm: UpdateUserAccountTypeRequest) => {
    setAccountTypeForm((currentForm) => {
      if (!currentForm) return nextForm;

      const accountDetailsChanged = (Object.keys(nextForm) as Array<keyof UpdateUserAccountTypeRequest>)
        .some((field) => field !== 'declarationAccepted' && nextForm[field] !== currentForm[field]);

      if (!accountDetailsChanged) return nextForm;

      const matchesSavedAccountDetails = Boolean(
        savedAccountTypeForm
        && (Object.keys(nextForm) as Array<keyof UpdateUserAccountTypeRequest>)
          .every((field) => field === 'declarationAccepted' || nextForm[field] === savedAccountTypeForm[field])
      );

      return {
        ...nextForm,
        declarationAccepted: matchesSavedAccountDetails
          ? Boolean(savedAccountTypeForm?.declarationAccepted)
          : false,
      };
    });
    setAccountTypeErrors({});
  };

  const notificationItems = [
    {
      key: 'marketingEmails' as const,
      title: t.settings.marketingEmails,
      description: t.settings.marketingEmailsDescription,
      icon: Mail,
    },
    {
      key: 'productActivatedNotifications' as const,
      title: t.settings.announcementActivated,
      description: t.settings.announcementActivatedDescription,
      icon: BadgeCheck,
    },
    {
      key: 'productExpiredNotifications' as const,
      title: t.settings.announcementExpired,
      description: t.settings.announcementExpiredDescription,
      icon: Clock3,
    },
    {
      key: 'chatNotifications' as const,
      title: t.settings.chatNotifications,
      description: t.settings.chatNotificationsDescription,
      icon: MessageCircle,
    },
  ];
  const activeNotificationCount = notificationItems.filter(({ key }) => settings[key]).length;
  const activeSummary = t.settings.activeSummary
    .replace('{active}', String(activeNotificationCount))
    .replace('{total}', String(notificationItems.length));
  const activeAccountTypeLabel = !hasSelectedAccountType
    ? t.settings.accountTypeNotSelected
    : accountTypeForm?.accountType === 'COMPANY'
      ? t.accountType.companyPublicLabel
      : t.accountType.privatePublicLabel;

  if (!isLoggedIn && !isAuthChecking) {
    onNavigateToLogin();
    return null;
  }

  if (loading) {
    return (
      <div className="settings-page">
        <Header
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          onNavigateToLogin={onNavigateToLogin}
          onNavigateToRegister={onNavigateToRegister}
          onNavigateToHome={onNavigateToHome}
          onLogout={onLogout}
          onProfileClick={onNavigateToProfile}
          onLocationsClick={onNavigateToLocations}
          onMessagesClick={onNavigateToMessages}
          onAnnouncementsClick={onNavigateToMyAnnouncements}
          onAdministrationClick={onNavigateToAdministration}
          onNavigateToCreateProduct={onNavigateToCreateProduct}
        />
        <div className="settings-container">
          <UserSectionSidebar
            activeItem="settings"
            onProfileClick={onNavigateToProfile}
            onLocationsClick={onNavigateToLocations}
            onMessagesClick={() => onNavigateToMessages?.()}
            onAnnouncementsClick={onNavigateToMyAnnouncements}
            onSettingsClick={() => {}}
          />
          <main className="settings-main">
            <h1 className="settings-title">{t.settings.title}</h1>
            <LoadingIndicator message={t.settings.loading} variant="page" className="settings-loading" />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onAdministrationClick={onNavigateToAdministration}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />

      <div className="settings-container">
        <UserSectionSidebar
          activeItem="settings"
          onProfileClick={onNavigateToProfile}
          onLocationsClick={onNavigateToLocations}
          onMessagesClick={() => onNavigateToMessages?.()}
          onAnnouncementsClick={onNavigateToMyAnnouncements}
          onSettingsClick={() => {}}
        />

        <main className="settings-main">
          <section className="settings-top-header" aria-labelledby="settings-title">
            <div className="settings-heading">
              <span className="settings-eyebrow">
                <Sparkles size={15} aria-hidden />
                {t.settings.eyebrow}
              </span>
              <h1 id="settings-title" className="settings-title">{t.settings.title}</h1>
              <p className="settings-subtitle">{t.settings.subtitle}</p>
            </div>
            <div
              className="settings-overview"
              aria-label={activeSettingsSection === 'account' ? activeAccountTypeLabel : activeSummary}
            >
              <span className="settings-overview-icon">
                {activeSettingsSection === 'account'
                  ? <UsersRound size={20} aria-hidden />
                  : <SlidersHorizontal size={20} aria-hidden />}
              </span>
              <div>
                <strong className={activeSettingsSection === 'account' ? 'settings-overview-account-type' : undefined}>
                  {activeSettingsSection === 'account' ? activeAccountTypeLabel : activeNotificationCount}
                </strong>
                <small>
                  {activeSettingsSection === 'account' ? t.settings.accountTypeTitle : activeSummary}
                </small>
              </div>
            </div>
          </section>

          <div className="settings-content">
            <div className="settings-section-tabs" role="tablist" aria-label={t.settings.title}>
              <button
                type="button"
                role="tab"
                aria-selected={activeSettingsSection === 'account'}
                className={activeSettingsSection === 'account' ? 'active' : ''}
                onClick={() => setActiveSettingsSection('account')}
              >
                <UsersRound size={17} aria-hidden />
                <span>{t.settings.accountTypeTitle}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeSettingsSection === 'notifications'}
                className={activeSettingsSection === 'notifications' ? 'active' : ''}
                onClick={() => setActiveSettingsSection('notifications')}
              >
                <Bell size={17} aria-hidden />
                <span>{t.settings.notificationsTitle}</span>
              </button>
            </div>

            {activeSettingsSection === 'account' ? (
            <section className="settings-section settings-account-type-section" role="tabpanel">
              <div className="settings-section-heading">
                <span className="settings-section-icon"><UsersRound size={20} aria-hidden /></span>
                <div>
                  <h2 className="settings-section-title">{t.settings.accountTypeTitle}</h2>
                  <p className="settings-section-description">{t.settings.accountTypeDescription}</p>
                </div>
              </div>

              {!hasSelectedAccountType && (
                <div className="settings-account-type-empty" role="status">
                  <CircleHelp size={20} aria-hidden />
                  <div>
                    <strong>{t.settings.accountTypeEmptyTitle}</strong>
                    <p>{t.settings.accountTypeEmptyDescription}</p>
                  </div>
                </div>
              )}

              {accountTypeForm && (
                <AccountTypeForm
                  value={accountTypeForm}
                  onChange={handleAccountTypeFormChange}
                  selectedAccountType={hasSelectedAccountType ? accountTypeForm.accountType : null}
                  onTypeSelect={() => setHasSelectedAccountType(true)}
                  disabled={isSavingAccountType}
                  errors={accountTypeErrors}
                />
              )}

              <div className="settings-account-type-actions">
                <p>{t.settings.accountTypeNotice}</p>
                <button
                  type="button"
                  className="settings-account-type-save"
                  onClick={() => void handleAccountTypeSave()}
                  disabled={!accountTypeHasChanges || isSavingAccountType}
                >
                  {isSavingAccountType ? t.settings.accountTypeSaving : t.settings.accountTypeSave}
                </button>
              </div>
            </section>
            ) : (
            <section className="settings-section" role="tabpanel">
              <div className="settings-section-heading">
                <span className="settings-section-icon"><Bell size={20} aria-hidden /></span>
                <div>
                  <h2 className="settings-section-title">{t.settings.notificationsTitle}</h2>
                  <p className="settings-section-description">{t.settings.notificationsDescription}</p>
                </div>
              </div>

              <div className="settings-items">
                {notificationItems.map(({ key, title, description, icon: Icon }) => (
                  <div className="settings-item" key={key}>
                    <span className="settings-item-icon" aria-hidden><Icon size={19} /></span>
                    <div className="settings-item-info">
                      <h3 className="settings-item-title">{title}</h3>
                      <p className="settings-item-description">{description}</p>
                    </div>
                    <label className="settings-toggle">
                      <input
                        type="checkbox"
                        checked={settings[key]}
                        onChange={() => handleToggle(key)}
                        aria-label={title}
                      />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  </div>
                ))}
                {isAnalyticsConfigured() && (
                  <div className="settings-item">
                    <span className="settings-item-icon" aria-hidden><Cookie size={19} /></span>
                    <div className="settings-item-info">
                      <h3 className="settings-item-title">{t.settings.cookiePreferencesTitle}</h3>
                      <p className="settings-item-description">{t.settings.cookiePreferencesDescription}</p>
                    </div>
                    <button
                      type="button"
                      className="settings-account-type-save"
                      onClick={openCookiePreferences}
                      aria-haspopup="dialog"
                    >
                      {t.cookieConsent.preferences}
                    </button>
                  </div>
                )}
              </div>
            </section>
            )}
          </div>
        </main>
      </div>

      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
        onNavigateToAbout={onNavigateToAbout}
      />
    </div>
  );
};

export default Settings;
